import React from "react";
import DatePicker from "react-datepicker";
import Select, { Creatable } from "react-select";
import { Popover, OverlayTrigger } from "react-bootstrap";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { TZDate } from "@date-fns/tz";
import { Button, ButtonGroup } from "aible";
import * as Expressions from "types/expression";
import * as ExpressionUtils from "utils/expression";
import { customMapper, mapToSelectOption, mapToSelectOptions, selectOnChangeWrapper, selectOnChangeWrapperMulti } from "utils/select";
import { LineInput } from "components/Inputs/LineInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faFont, faHashtag, faLink } from "@fortawesome/free-solid-svg-icons";
import styles from "./style.module.css";
const DataTypeIcon = ({ output }) => {
    if (output.type == "DATE") {
        return React.createElement(FontAwesomeIcon, { icon: faCalendarAlt });
    }
    else if (output.type == "NUMERIC") {
        return React.createElement(FontAwesomeIcon, { icon: faHashtag });
    }
    else {
        return React.createElement(FontAwesomeIcon, { icon: faFont });
    }
};
const Tooltip = ({ title, body }) => {
    return (React.createElement("div", { onClick: (e) => e.stopPropagation() },
        React.createElement(OverlayTrigger, { placement: "top", trigger: ["hover"], rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top", title: React.createElement("strong", { style: { fontSize: "13px" } }, title) },
                React.createElement("div", { style: { maxWidth: "400px" } }, body)) },
            React.createElement("div", { className: styles.tooltipIcon + " " + styles.isInfo },
                React.createElement(FontAwesomeIcon, { icon: faInfo })))));
};
export const Labeled = ({ label, children, style, isBold, tooltip }) => {
    const s = { maxWidth: "200px" };
    return (React.createElement("div", { style: style },
        React.createElement("div", { className: styles.topLabel, title: label, style: isBold ? { ...s, fontWeight: "bold" } : s },
            label,
            tooltip && React.createElement(Tooltip, { title: label, body: tooltip })),
        children));
};
const ALLOWED_OUTPUTS = [{ type: "TEXT" }, { type: "NUMERIC" }, { type: "DATE" }];
export const ToggleableColumnOrScalarInput = ({ value, defaultValue, onChange, isDisabled, context, output, isCreateable }) => {
    const isScalar = Expressions.isScalar(value);
    const buttons = (output === undefined ? ALLOWED_OUTPUTS : [output]).map((o) => {
        const isActive = isScalar && (o.type === output?.type || ExpressionUtils.isOfType(value, o.type));
        return (React.createElement(Button, { key: o.type, active: isActive, disabled: isDisabled, style: { paddingLeft: "5px", paddingRight: "5px" }, onClick: () => {
                if (!isActive) {
                    if (output === undefined) {
                        onChange(ExpressionUtils.getDefaultValue(o));
                    }
                    else {
                        onChange(undefined);
                    }
                }
            }, title: `Specify a fixed ${o.type.toLowerCase()} value` },
            React.createElement(DataTypeIcon, { output: o })));
    });
    buttons.push(React.createElement(Button, { active: !isScalar, disabled: isDisabled, style: { paddingLeft: "5px", paddingRight: "5px" }, onClick: () => isScalar && onChange({ column: "" }), title: "Specify a lookup to another column" },
        React.createElement(FontAwesomeIcon, { icon: faLink })));
    return (React.createElement("div", { style: { display: "flex" } },
        React.createElement(ColumnOrScalarInput, { value: value, defaultValue: defaultValue, onChange: (x) => onChange(x), isDisabled: isDisabled, context: context, output: output, isCreateable: isCreateable }),
        React.createElement(ButtonGroup, { style: { marginLeft: "5px", height: "38px" } }, ...buttons)));
};
export const ColumnOrScalarInput = ({ value, defaultValue, onChange, isDisabled, context, output, isCreateable }) => {
    if (Expressions.isScalar(value)) {
        const safeDefaultValue = Expressions.isScalar(defaultValue) ? defaultValue : undefined;
        return (React.createElement(ScalarValueInput, { value: value, defaultValue: safeDefaultValue, onChange: onChange, isDisabled: isDisabled, context: context, output: output }));
    }
    else {
        const safeDefaultValue = Expressions.isColumn(defaultValue) ? defaultValue : undefined;
        return (React.createElement(ColumnValueInput, { value: value, defaultValue: safeDefaultValue, onChange: onChange, isDisabled: isDisabled, context: context, output: output, isCreateable: isCreateable }));
    }
};
export const ScalarValueInput = ({ value, defaultValue, onChange, isDisabled, output }) => {
    if (typeof value === "boolean" || (value === undefined && output?.type === "BOOLEAN")) {
        const safeDefaultValue = typeof defaultValue === "boolean" ? defaultValue : undefined;
        return (React.createElement(BooleanValueInput, { value: value, defaultValue: safeDefaultValue, onChange: onChange, isDisabled: isDisabled }));
    }
    else if (typeof value === "number" || (value === undefined && output?.type === "NUMERIC")) {
        const safeDefaultValue = typeof defaultValue === "number" ? defaultValue : undefined;
        return (React.createElement(NumericValueInput, { value: value, defaultValue: safeDefaultValue, onChange: onChange, isDisabled: isDisabled }));
    }
    else if (typeof value === "string" ||
        (value === undefined && (output === undefined || output?.type === "TEXT" || output?.type === "DATE"))) {
        const safeDefaultValue = typeof defaultValue === "string" ? defaultValue : undefined;
        if (output?.type == "DATE" || ExpressionUtils.isOfType(value, "DATE")) {
            return (React.createElement(DateValueInput, { value: value, defaultValue: safeDefaultValue, onChange: onChange, isDisabled: isDisabled }));
        }
        else if (output?.type == "NUMERIC") {
            const parsedValue = Number(value);
            const parsedDefaultValue = Number(value);
            return (React.createElement(NumericValueInput, { value: isNaN(parsedValue) ? undefined : parsedValue, defaultValue: isNaN(parsedDefaultValue) ? undefined : parsedDefaultValue, onChange: onChange, isDisabled: isDisabled }));
        }
        else {
            const values = (output?.values ?? []).map(String).filter((value) => value !== "{Other}" && value !== "{Missing}");
            if (values.length == 0) {
                return (React.createElement(TextValueInput, { value: value, defaultValue: safeDefaultValue, onChange: onChange, isDisabled: isDisabled }));
            }
            return (React.createElement(CategoricalValueInput, { value: value, defaultValue: safeDefaultValue, values: values, onChange: onChange, isDisabled: isDisabled }));
        }
    }
    else if (value === undefined || value == null) {
        return React.createElement("div", null);
    }
    throw Error(`unexpected value: ${value}`);
};
export const ColumnValueInput = ({ value, defaultValue, onChange, isDisabled, context, output, isCreateable, isWide }) => {
    const Dropdown = (isCreateable === true ? Creatable : Select);
    const column = (value ?? defaultValue ?? {}).column;
    const columns = Object.entries(context)
        .filter(([_, o]) => output === undefined || o.type == output.type)
        .map(([name, _]) => name);
    const isInvalid = !isCreateable && column !== undefined && column !== "" && context[column] === undefined;
    const accentColor = column !== undefined && column !== "" ? "#DC2626" : "#2684FF";
    const selectStyles = !isInvalid
        ? undefined
        : {
            control: (base, _) => ({
                ...base,
                "&:hover": { borderColor: accentColor },
                borderColor: accentColor,
                boxShadow: `0 0 0 1px ${accentColor}`
            })
        };
    return (React.createElement("div", { className: styles.columnInput, style: isWide ? { width: "400px" } : {} },
        React.createElement(Dropdown, { value: mapToSelectOption(column === "" ? undefined : column), onChange: selectOnChangeWrapper((x) => onChange({ column: x ?? "" })), options: mapToSelectOptions(columns), placeholder: "Select Column (Required)", label: "Column", menuPlacement: "bottom", isDisabled: isDisabled, styles: selectStyles, isClearable: true })));
};
export const ColumnValueMultiInput = ({ value, defaultValue, onChange, isDisabled, context, output, isCreateable, isWide }) => {
    const Dropdown = (isCreateable === true ? Creatable : Select);
    const columns = (value ?? defaultValue ?? []).map((x) => x.column).filter((c) => c !== "");
    const options = Object.entries(context)
        .filter(([_, o]) => output === undefined || o.type == output.type)
        .map(([name, _]) => name);
    const isInvalid = !isCreateable && columns?.length > 0 && columns.some((c) => context[c] === undefined);
    const validate = isCreateable ? (_) => true : (x) => x === "" || context[x] !== undefined;
    const selectStyles = !isInvalid
        ? undefined
        : {
            multiValue: (styles, { data }) => {
                if (validate(data.value)) {
                    return styles;
                }
                return {
                    ...styles,
                    border: "1px solid #DE350B",
                    boxShadow: `0 0 0 1px #DE350B`,
                    backgroundColor: "#FFBDAD"
                };
            },
            multiValueLabel: (styles, { data }) => validate(data.value)
                ? styles
                : {
                    ...styles,
                    color: "#DE350B"
                },
            multiValueRemove: (styles, { data }) => validate(data.value)
                ? styles
                : {
                    ...styles,
                    color: "#DE350B",
                    ":hover": {
                        backgroundColor: "#DE350B",
                        color: "#FFBDAD"
                    }
                }
        };
    return (React.createElement("div", { className: styles.columnInput, style: isWide ? { width: "400px" } : {} },
        React.createElement(Dropdown, { value: mapToSelectOptions(columns.length === 0 ? undefined : columns), onChange: selectOnChangeWrapperMulti((x) => onChange(x === undefined || x === null ? undefined : x.map((c) => Expressions.column(c)))), options: mapToSelectOptions(options), placeholder: "Select Columns (Required)", label: "Columns", menuPlacement: "bottom", isDisabled: isDisabled, styles: selectStyles, isClearable: true, isMulti: true })));
};
export function CategoricalValueInput({ value, defaultValue, values, onChange, isDisabled, selectOnly, isInvalid }) {
    return (React.createElement(MappedCategoricalValueInput, { value: value, defaultValue: defaultValue, values: values, onChange: onChange, isDisabled: isDisabled, selectOnly: selectOnly, mapper: customMapper((s) => String(s)), isInvalid: isInvalid }));
}
export function MappedCategoricalValueInput({ value, defaultValue, mapper, values, onChange, isDisabled, selectOnly, isInvalid, placeholder }) {
    const Dropdown = (selectOnly === true ? Select : Creatable);
    const createableProps = {
        noOptionsMessage: (_) => "Type in a value"
    };
    const selectStyles = !isInvalid
        ? undefined
        : {
            control: (base, _) => ({
                ...base,
                "&:hover": { borderColor: "#DC2626" },
                borderColor: "#DC2626",
                boxShadow: `0 0 0 1px #DC2626`
            })
        };
    return (React.createElement("div", { className: styles.columnInput },
        React.createElement(Dropdown, { value: mapper.map(value ?? defaultValue ?? undefined), onChange: selectOnChangeWrapper(onChange), options: mapper.mapAll(values), placeholder: values.length > 0 ? (placeholder ?? "Select Value") : "", menuPlacement: "bottom", label: "Value", isDisabled: isDisabled, styles: selectStyles, ...(selectOnly ? {} : createableProps) })));
}
const BOOLEAN_MAPPER = customMapper((d) => (d ? "True" : "False"));
export function BooleanValueInput({ value, defaultValue, onChange, isDisabled }) {
    return (React.createElement("div", { className: styles.columnInput },
        React.createElement(Select, { value: BOOLEAN_MAPPER.map(value === undefined ? defaultValue : value), onChange: selectOnChangeWrapper(onChange), options: BOOLEAN_MAPPER.mapAll([true, false]), menuPlacement: "bottom", label: "Value", isDisabled: isDisabled })));
}
export const NumericValueInput = ({ value, defaultValue, onChange, isDisabled }) => {
    return (React.createElement("div", { className: styles.columnInput },
        React.createElement(LineInput, { isMediumBox: true, value: value === undefined ? (defaultValue === undefined ? "" : defaultValue) : value, onChange: (x) => {
                const numeric = Number(x);
                if (!isNaN(numeric)) {
                    onChange(numeric);
                }
                else {
                    onChange(0);
                }
            }, inputStyle: { width: "100%", height: "100%", fontSize: "12px" }, style: { width: "200px", height: "38px" }, isDisabled: isDisabled, placeholder: "Specify Value" })));
};
export const TextValueInput = ({ value, defaultValue, onChange, isDisabled }) => {
    return (React.createElement("div", { className: styles.columnInput },
        React.createElement(LineInput, { isMediumBox: true, value: value === undefined ? defaultValue : value, onChange: (x) => onChange(x), inputStyle: { width: "100%", height: "100%", fontSize: "12px" }, style: { width: "200px", height: "38px" }, isDisabled: isDisabled, placeholder: "Specify Value (Required)" })));
};
export const AliasInput = ({ value, defaultValue, valueFormatter, onChange, isDisabled, placeholder }) => {
    const formatter = valueFormatter ?? ((x) => x);
    return (React.createElement("div", { className: styles.columnInput },
        React.createElement(LineInput, { isMediumBox: true, value: value === undefined || value === "" ? (defaultValue ?? value) : value, onChange: (x) => onChange(formatter(x)), inputStyle: { width: "100%", height: "100%", fontSize: "12px" }, style: { width: "200px", height: "38px" }, isDisabled: isDisabled, placeholder: placeholder ?? "New Column Alias (Required)" })));
};
export const DateValueInput = ({ value, defaultValue, onChange, isDisabled, start, end, min, isStart, isEnd, placeholder }) => {
    const toDate = (x, defaultX, allowUndefined = true) => {
        if (x === undefined) {
            if (defaultX === undefined) {
                if (allowUndefined) {
                    return undefined;
                }
                const date = new Date();
                return new TZDate(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()), "UTC");
            }
            else {
                x = defaultX;
            }
        }
        return new TZDate(x, "UTC");
    };
    return (React.createElement("div", { className: styles.columnInput },
        React.createElement(DatePicker, { selected: toDate(value, defaultValue, false), onChange: (x) => {
                if (x == undefined) {
                    onChange(undefined);
                }
                else {
                    // Convert Timestamp to YYYY-MM-DD string
                    const nDate = new TZDate(x, "UTC").toISOString().split("T")[0];
                    onChange(nDate);
                }
            }, className: styles.datePickerInput, disabled: isDisabled, minDate: toDate(min), startDate: toDate(start), endDate: toDate(end), selectsStart: isStart === true, selectsEnd: isEnd === true, placeholderText: placeholder ?? "Select Date" })));
};
